import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'
import { Spinner } from './Spinner'

type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'ghost' | 'ghostOnInk' | 'vjal'
type ButtonSize = 'sm' | 'md' | 'lg'

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-mustard text-ink hover:bg-mustard-600 active:bg-mustard-600',
  secondary: 'bg-white text-strong border border-line-strong hover:bg-whisper active:bg-stone',
  success: 'bg-ok-ink text-white hover:brightness-90 active:brightness-90',
  danger: 'bg-err-ink text-white hover:brightness-90 active:brightness-90',
  ghost: 'bg-transparent text-strong hover:bg-whisper active:bg-stone',
  ghostOnInk: 'bg-transparent text-on-ink-soft hover:bg-veil hover:text-on-ink active:bg-veil-strong',
  vjal: 'bg-mustard text-ink hover:bg-mustard-600 active:bg-mustard-600',
}

const focusRings: Record<ButtonVariant, string> = {
  primary: 'focus-visible:shadow-focus',
  secondary: 'focus-visible:shadow-focus',
  success: 'focus-visible:shadow-focus',
  danger: 'focus-visible:shadow-focus',
  ghost: 'focus-visible:shadow-focus',
  ghostOnInk: 'focus-visible:shadow-focus-dark',
  vjal: 'focus-visible:shadow-focus',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant. `variant="vjal"` is deprecated and aliases to `primary`. */
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  isLoading?: boolean
  /** Use with `variant="ghost"` for ink/dark surfaces. Replaces `variant="ghostOnInk"`. */
  onInk?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  className?: string
  children?: ReactNode
}

let didWarnDeprecatedVjalVariant = false

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    type = 'button',
    disabled,
    loading = false,
    isLoading = false,
    onInk = false,
    leftIcon,
    rightIcon,
    className = '',
    children,
    ...props
  },
  ref,
) {
  const showLoading = loading || isLoading
  const resolvedVariant = resolveButtonVariant(variant, onInk)

  if (variant === 'vjal') warnDeprecatedVjalVariantOnce()

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || showLoading}
      aria-busy={showLoading || undefined}
      className={cn(
        'inline-flex min-h-control items-center justify-center font-medium rounded-control coarse:min-h-tap',
        'transition-colors duration-fast ease-out-soft focus-visible:outline-none active:translate-y-px',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        focusRings[resolvedVariant],
        variants[resolvedVariant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {showLoading ? <ButtonSpinner /> : leftIcon}
      {children}
      {!showLoading && rightIcon}
    </button>
  )
})

function ButtonSpinner() {
  return (
    <span aria-hidden="true">
      <Spinner size="sm" className="mr-2 text-current" label={null} />
    </span>
  )
}

function resolveButtonVariant(variant: ButtonVariant, onInk: boolean): ButtonVariant {
  if (variant === 'vjal') return 'primary'
  if (onInk && variant === 'ghost') return 'ghostOnInk'
  return variant
}

function warnDeprecatedVjalVariantOnce() {
  if (didWarnDeprecatedVjalVariant || !isDevRuntime()) return
  didWarnDeprecatedVjalVariant = true
  console.warn('variant=vjal is deprecated; use primary')
}

function isDevRuntime() {
  const metaEnv = (import.meta as ImportMeta & { env?: { DEV?: boolean; PROD?: boolean } }).env
  if (metaEnv?.PROD) return false
  if (metaEnv?.DEV) return true
  const nodeEnv = (globalThis as typeof globalThis & { process?: { env?: { NODE_ENV?: string } } }).process?.env
    ?.NODE_ENV
  return nodeEnv !== 'production'
}
